/** The two components more than one tab of the Sources screen draws: a state's glyph and the line
 * that says who bills a stream's meter. The words, the table of states and the navigation contract
 * they use are in `sourceModel.ts`; the comments are the originals, moved with the code.
 */

import { useContext, useState } from "react";

import type { SourceCoverage } from "../api/sources";
import { STATE, SourcesNavContext, jumpTo } from "./sourceModel";
import own from "./Sources.module.css";

/** The glyph for one state, drawn at the size of the word beside it. Decorative: the word is the
 * label, so a screen reader hears "Q2 missing" and not "warning circle, Q2 missing". */
export function StateGlyph({ state }: { state: string }) {
  const meaning = STATE[state];
  if (!meaning) return null;
  const Glyph = meaning.glyph;
  return <Glyph size={12} weight={meaning.weight} aria-hidden="true" className={own.glyph} />;
}


/** Who bills this stream's meter — **in words, and never as the absence of a supplier**.
 *
 * Three states and three sentences (ADR-0062). *Not recorded* says what it claims and what it does
 * not: nobody has told the product, which is not the same as nobody billing the meter. A meter that
 * predates the register is here, correctly, and the line is a fact to know rather than a fault —
 * hence plain ink and a word, not an alarm.
 *
 * The state is also on the element as an attribute, so a check reads the fact and not the colour.
 */
export function SupplyLine({ source }: { source: SourceCoverage }) {
  const { supply } = source;
  const nav = useContext(SourcesNavContext);
  const [gone, setGone] = useState(false);
  return (
    <p
      className={own.supply}
      data-testid={`supply-${source.source_id}`}
      data-supply={supply.state}
    >
      {supply.state === "recorded" ? (
        <>
          Billed by <strong>{supply.vendor_name}</strong> · account {supply.account_ref}
          {supply.account_id ? (
            <>
              {" "}
              {/* **A connection is a destination** (EXP-06): the account is a row in the register
                  below, so this goes there rather than leaving the reader to scroll and look. */}
              <button
                type="button"
                className={own.ownerAction}
                data-testid={`supply-open-${source.source_id}`}
                onClick={() =>
                  setGone(
                    !(nav ? nav.openAccount(supply.account_id as string) : jumpTo(`account-${supply.account_id}`)),
                  )
                }
              >
                Open the account
              </button>
              {gone ? <span className={own.formNote}> That account is not drawn on this page.</span> : null}
            </>
          ) : null}
        </>
      ) : supply.state === "not_recorded" ? (
        <>
          Who bills this meter is <span className={own.supplyNotRecorded}>not recorded</span>.
        </>
      ) : (
        <>Not tied to a meter, so no supply account applies.</>
      )}
    </p>
  );
}

